import { useState } from 'react';

import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { deleteEndorsement } from '../../api';

type UseDeleteEndorsementProps = {
	petitionId: string;
	onSuccess?: () => void | Promise<void>;
};

type UseDeleteEndorsementReturn = ModelHookResult<
	{
		isDeleting: boolean;
		error: string | null;
	},
	{
		deleteEndorsement: (endorsementId: string) => Promise<boolean>;
	}
>;

export function useDeleteEndorsement({ petitionId, onSuccess }: UseDeleteEndorsementProps): UseDeleteEndorsementReturn {
	const utilityContext = useUtilityContext();
	const [isDeleting, setIsDeleting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleDeleteEndorsement = async (endorsementId: string): Promise<boolean> => {
		setIsDeleting(true);
		setError(null);

		try {
			const response = await deleteEndorsement(
				{
					endorsementId,
					petitionId,
				},
				utilityContext,
			);

			if (response.success) {
				await onSuccess?.();
				return true;
			}

			const errorMessage = response.errorMessage ?? 'Failed to delete endorsement';
			setError(errorMessage);

			return false;
		} catch (e) {
			const errorMessage = e instanceof Error ? e.message : 'Error deleting endorsement';
			setError(errorMessage);

			return false;
		} finally {
			setIsDeleting(false);
		}
	};

	return {
		data: {
			isDeleting,
			error,
		},
		actions: {
			deleteEndorsement: handleDeleteEndorsement,
		},
	};
}
